/**
 * Account-page card for the user's institution (business/SACCO/group).
 *
 * A person opens the account; when they act for an institution they add
 * it here with its own KYC: registered name, registration number, TIN,
 * certificate of incorporation (+ optional authorisation letter). An
 * admin then verifies — mirrored from the personal-KYC two-stage flow.
 *
 * Endpoints: GET/POST /api/business/, PATCH /api/business/{id}/
 * (multipart for the documents).
 */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Input, Modal, Tag, Upload, message } from "antd";
import type { UploadFile } from "antd";
import { BusinessOutline, CloudUploadOutline } from "@raresail/react-ionicons";
import useGet from "../../../hooks/common/useGet";
import useSet from "../../../hooks/common/useSet";
import { API_BUSINESS } from "../../../constants/apis";
import { PRIMARY_COLOR, primary10 } from "../../../constants/colors";

type TInstitution = {
  id: string;
  business_name?: string;
  registration_number?: string;
  tin_number?: string;
  business_email?: string;
  business_phone?: string;
  industry?: string;
  certificate_of_incorporation?: string | null;
  authorization_letter?: string | null;
  is_verified?: boolean;
  kyc_status?: "missing" | "incomplete" | "pending_review" | "verified";
  kyc_missing_fields?: string[];
};

const STATUS_META: Record<string, { color: string; label: string }> = {
  verified: { color: "green", label: "Verified" },
  pending_review: { color: "gold", label: "Under review" },
  incomplete: { color: "orange", label: "Action needed" },
  missing: { color: "default", label: "Not started" },
};

export const InstitutionCard = () => {
  const { loadData, data, loading } = useGet();
  const { uploadData, submitting } = useSet();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [certificate, setCertificate] = useState<UploadFile[]>([]);
  const [authLetter, setAuthLetter] = useState<UploadFile[]>([]);

  const refresh = () => loadData({ api: API_BUSINESS });
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const institution: TInstitution | null =
    (Array.isArray(data) ? data[0] : data?.results?.[0]) ?? null;
  const status = STATUS_META[institution?.kyc_status || "missing"];

  const openModal = () => {
    setForm({
      business_name: institution?.business_name || "",
      registration_number: institution?.registration_number || "",
      tin_number: institution?.tin_number || "",
      business_email: institution?.business_email || "",
      business_phone: institution?.business_phone || "",
      industry: institution?.industry || "",
    });
    setCertificate([]);
    setAuthLetter([]);
    setOpen(true);
  };

  const set = (k: string) => (e: any) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = () => {
    if (!form.business_name?.trim()) {
      message.error("Enter the registered business name.");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, v);
    });
    const cert = certificate[0]?.originFileObj;
    if (cert) fd.append("certificate_of_incorporation", cert);
    const letter = authLetter[0]?.originFileObj;
    if (letter) fd.append("authorization_letter", letter);

    uploadData({
      api: API_BUSINESS,
      id: institution?.id,
      method: institution ? "PATCH" : "POST",
      params: fd,
      type: "multipart/form-data",
      successMessage: {
        message: institution
          ? "Institution updated"
          : "Institution added — our team will review the documents",
        duration: 5,
      },
      onSuccess: () => {
        setOpen(false);
        refresh();
      },
    });
  };

  const missing = institution?.kyc_missing_fields || [];

  return (
    <>
      <CardBox>
        <IconBubble>
          <BusinessOutline fontSize={20} color={PRIMARY_COLOR} />
        </IconBubble>
        <div className="flexGrow">
          <div className="flexNullCenter gap8">
            <div className="font14 semiBold textDark">
              {institution?.business_name || "Institution"}
            </div>
            <Tag color={status.color} style={{ marginInlineEnd: 0 }}>
              {status.label}
            </Tag>
          </div>
          <div className="font12 grey" style={{ lineHeight: 1.4 }}>
            {institution
              ? missing.length
                ? `Missing: ${missing
                    .map((m) => m.replace(/_/g, " "))
                    .join(", ")}`
                : institution.is_verified
                ? "Your institution can transact on the platform."
                : "Documents submitted — awaiting review."
              : "Investing or borrowing as a company, SACCO or group? Add its registration details and documents."}
          </div>
        </div>
        <Button
          type={institution ? "default" : "primary"}
          loading={loading}
          onClick={openModal}
        >
          {institution ? "Edit" : "Add institution"}
        </Button>
      </CardBox>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={institution ? "Edit institution" : "Add your institution"}
        footer={null}
        width={480}
      >
        <div className="flexColumn gap10">
          <Field label="Registered business name *">
            <Input
              value={form.business_name}
              onChange={set("business_name")}
              placeholder="Acme Traders Ltd"
            />
          </Field>
          <Field label="Registration number *">
            <Input
              value={form.registration_number}
              onChange={set("registration_number")}
              placeholder="e.g. 80020001234567"
            />
          </Field>
          <Field label="TIN *">
            <Input
              value={form.tin_number}
              onChange={set("tin_number")}
              placeholder="URA Tax Identification Number"
            />
          </Field>
          <Field label="Business email (optional)">
            <Input
              value={form.business_email}
              onChange={set("business_email")}
              type="email"
            />
          </Field>
          <Field label="Business phone (optional)">
            <Input
              value={form.business_phone}
              onChange={set("business_phone")}
              placeholder="+256700123456"
            />
          </Field>
          <Field label="Industry (optional)">
            <Input value={form.industry} onChange={set("industry")} />
          </Field>

          <Field
            label={`Certificate of incorporation *${
              institution?.certificate_of_incorporation
                ? " (already on file — upload to replace)"
                : ""
            }`}
          >
            <Upload
              maxCount={1}
              fileList={certificate}
              beforeUpload={() => false}
              onChange={({ fileList }) => setCertificate(fileList)}
            >
              <Button icon={<CloudUploadOutline fontSize={16} />}>
                Choose file
              </Button>
            </Upload>
          </Field>
          <Field
            label={`Authorisation letter (optional)${
              institution?.authorization_letter
                ? " (already on file — upload to replace)"
                : ""
            }`}
          >
            <Upload
              maxCount={1}
              fileList={authLetter}
              beforeUpload={() => false}
              onChange={({ fileList }) => setAuthLetter(fileList)}
            >
              <Button icon={<CloudUploadOutline fontSize={16} />}>
                Choose file
              </Button>
            </Upload>
          </Field>

          <Button type="primary" block loading={submitting} onClick={submit}>
            {institution ? "Save changes" : "Submit for review"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

const Field = ({ label, children }: any) => (
  <div>
    <div className="font12 grey mb5">{label}</div>
    {children}
  </div>
);

const CardBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid ${primary10};
  border-radius: 12px;
`;

const IconBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${primary10};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;
